import { useState, useEffect, useRef } from 'react';
import { api } from '@/lib/api';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import { Image as ImageIcon } from 'lucide-react';
import { useCachedMedia } from '@/hooks/useCachedMedia';
import { useLanguage } from '@/contexts/LanguageContext';
import { UI_TRANSLATIONS } from '@/lib/i18n';

interface FeatureImageProps {
  municipalityId: string;
  alt: string;
}

interface FeatureCarouselImage {
  id: string;
  image_url: string;
  display_order: number;
}

const isVideoUrl = (url: string | null) => {
  if (!url) return false;
  if (url.startsWith('data:video/')) return true;
  const cleanUrl = url.split('?')[0].toLowerCase();
  return (
    cleanUrl.endsWith('.mp4') ||
    cleanUrl.endsWith('.webm') ||
    cleanUrl.endsWith('.ogg') ||
    cleanUrl.endsWith('.mov') ||
    url.includes('video')
  );
};

interface CachedMediaItemProps {
  url: string;
  alt: string;
  isVideo: boolean;
  isActive: boolean;
  onVideoEnded?: () => void;
  loopVideo?: boolean;
}

const CachedMediaItem = ({
  url,
  alt,
  isVideo,
  isActive,
  onVideoEnded,
  loopVideo
}: CachedMediaItemProps) => {
  const { cachedUrl } = useCachedMedia(url);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (!isVideo || !videoRef.current) return;

    if (isActive) {
      videoRef.current.currentTime = 0;
      videoRef.current
        .play()
        .catch((err) => console.log('Autoplay de vídeo bloqueado o interrumpido:', err));
    } else {
      videoRef.current.pause();
    }
  }, [isActive, isVideo, cachedUrl]);

  if (isVideo) {
    return (
      <video
        ref={videoRef}
        src={cachedUrl}
        className="bottom-image w-full h-full object-cover"
        muted
        playsInline
        loop={loopVideo}
        onEnded={onVideoEnded}
      />
    );
  }

  return (
    <img
      src={cachedUrl}
      alt={alt}
      className="bottom-image w-full h-full object-cover"
      loading="eager"
      decoding="sync"
    />
  );
};

export const FeatureImage = ({ municipalityId, alt }: FeatureImageProps) => {
  const { language } = useLanguage();
  const translations = UI_TRANSLATIONS[language];
  const [images, setImages] = useState<FeatureCarouselImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [apiInstance, setApiInstance] = useState<CarouselApi>();
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const isVisible = useIntersectionObserver(containerRef, {
    threshold: 0.1,
    rootMargin: '100px'
  });

  useEffect(() => {
    loadImages();
    // Recargar al enfocar la pestaña
    const handleFocus = () => loadImages();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [municipalityId]);

  const loadImages = async () => {
    try {
      const data = await api.carouselImages.list(municipalityId);
      setImages((data || []).filter((img: any) => !img.is_hidden));
    } catch (error) {
      console.error('Error al cargar elementos del carrusel:', error);
    } finally {
      setLoading(false);
    }
  };

  // Sincronizar el slide activo de Embla
  useEffect(() => {
    if (!apiInstance) return;

    const onSelect = () => {
      setActiveIndex(apiInstance.selectedScrollSnap());
    };

    apiInstance.on("select", onSelect);
    setActiveIndex(apiInstance.selectedScrollSnap());

    return () => {
      apiInstance.off("select", onSelect);
    };
  }, [apiInstance]);

  // Controlar autoplay de imágenes y retrasos
  useEffect(() => {
    if (!apiInstance || images.length <= 1) return;

    const currentItem = images[activeIndex];
    if (!currentItem) return;

    const isVideo = isVideoUrl(currentItem.image_url);

    // Si el elemento actual es un vídeo, no programamos temporizador.
    // El evento 'onEnded' del reproductor de vídeo avanzará al siguiente slide.
    if (isVideo) return;

    // Si es una imagen, avanzar automáticamente tras 5 segundos
    const timer = setTimeout(() => {
      apiInstance.scrollNext();
    }, 5000);

    return () => clearTimeout(timer);
  }, [apiInstance, activeIndex, images]);

  // Mostrar marcador de carga o vacío
  if (loading || images.length === 0) {
    return (
      <div ref={containerRef} className="bottom-image-container">
        <div className="w-full h-full flex items-center justify-center bg-muted/30 rounded-lg">
          <div className="text-center text-muted-foreground/50">
            <ImageIcon className="w-16 h-16 mx-auto mb-2" />
            {loading ? (
              <p className="text-sm">{translations.loading}</p>
            ) : (
              <p className="text-sm">{translations.noMedia}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="bottom-image-container">
      <Carousel
        opts={{
          align: "start",
          loop: true,
        }}
        setApi={setApiInstance}
        className="w-full h-full"
      >
        <CarouselContent className="h-full">
          {images.map((image, index) => {
            const isVideo = isVideoUrl(image.image_url);
            const isActive = index === activeIndex;
            const loopVideo = images.length === 1;

            return (
              <CarouselItem key={image.id} className="h-full">
                <CachedMediaItem
                  url={image.image_url}
                  alt={alt}
                  isVideo={isVideo}
                  isActive={isActive}
                  loopVideo={loopVideo}
                  onVideoEnded={() => {
                    if (apiInstance && images.length > 1) {
                      apiInstance.scrollNext();
                    }
                  }}
                />
              </CarouselItem>
            );
          })}
        </CarouselContent>
      </Carousel>
    </div>
  );
};
